import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import "./ProjectPage.css";

import backIcon from "../assets/icons/back.png";

import Navbar from "../components/Navbar.jsx";
import Footer from "../components/Footer.jsx";
import ImageWithFallback from "../components/ImageWithFallback";
import Select from "../components/Select";
import { useAuth } from "../features/auth";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useProjects } from "../features/catalog";
import {
  PROJECT_TABS,
  defaultTab,
  MediaBrowser,
  FinishesPanel,
  downloadFile,
} from "../features/project";
// Named import straight from the module (not the barrel above): isWebUrl is
// an internal guard, not part of features/project's public surface.
import { isWebUrl } from "../features/project/downloadFile";
// Straight from the module, not the features/experience barrel: the barrel's
// LoaderOverlay reads the URL at module load and belongs to the walkthrough chunk.
import { listScreenshots } from "../features/experience/screenshots";
import { getQuery } from "../lib/query";

import downloadIcon from "../assets/icons/download.png";
import vizwalkIcon from "../assets/logos/vizwalk-mark.png";
import openIcon from "../assets/icons/open.png";
import maximizeIcon from "../assets/icons/full-screen.png";

import indiaIcon from "../assets/icons/flag-in.png";
import usIcon from "../assets/icons/flag-us.png";

import downloadAllIcon from "../assets/icons/download.png";
import calendarIcon from "../assets/icons/calendar.png";
import refreshIcon from "../assets/icons/refresh.png";

// Open Vizwalk look: false = the existing card (icon tile + label), true = the
// solid ink button proposed in docs/design/mockups/ui-flow-header-options.html (option A).
const SOLID_LAUNCH_BUTTON = true;

function getRegionFromPath(pathname = "") {
  const parts = pathname.split("/").filter(Boolean);
  const first = (parts[0] || "").toLowerCase();
  return first === "us" ? "us" : "in";
}

function normalizeServer(v = "") {
  const s = String(v || "").trim().toLowerCase();
  if (s.startsWith("us") || s.includes("united")) return "us";
  if (s.startsWith("in") || s.includes("india")) return "india";
  return "india";
}

function formatSqft(n = "") {
  const s = String(n).replace(/,/g, "").trim();
  const val = Number(s);
  if (Number.isFinite(val) && val > 0) return `${val.toLocaleString()} sqft`;
  return n || "";
}

function prettyDate(ts) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "Date";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mn = String(d.getMinutes()).padStart(2, "0");
  const wk = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
  return `${dd}/${mm} ${wk} ${hh}:${mn}`;
}

// "V2 (current)", "V1 (retired)". A build with no label shows its key.
function buildOptionLabel(b, currentBuildId) {
  const name = b.label || b.key;
  if (b.id === currentBuildId) return `${name} (current)`;
  if (b.status === "retired") return `${name} (retired)`;
  return name;
}

export default function ProjectPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentRegion = getRegionFromPath(location.pathname);

  const { id: projectId } = useParams();
  const [activeTab, setActiveTab] = useState(() =>
    defaultTab({ tabParam: getQuery("tab", "") })
  );
  const { user, signOut } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);

  const [selectedServer, setSelectedServer] = useState(
    currentRegion === "us" ? "us" : "india"
  );

  const [screenshotsGroups, setScreenshotsGroups] = useState([]);
  const [initialLoadingShots, setInitialLoadingShots] = useState(false);
  const [isRefreshingShots, setIsRefreshingShots] = useState(false);

  const finishesPanelRef = useRef(null);
  const [isRefreshingFinishes, setIsRefreshingFinishes] = useState(false);

  const { projects, loading: loadingHeader } = useProjects(); // whole catalog — a build may be on any surface

  // Adapts the unified `Project` shape (src/features/catalog/useProjects.js) to the local header-item
  // shape this page has always rendered, so nothing below this needs to change.
  const catalogItems = useMemo(
    () =>
      projects.map((p) => ({
        id: p.id,
        media: p.media,
        currentBuildId: p.currentBuildId,
        buildKey: p.buildKey,
        builds: p.builds,
        server: normalizeServer(p.server),
        sbu: p.sbu,
        name: p.name,
        areaSqft: p.areaSqft ?? "",
        ConstructionType: p.constructionType,
        designStyle: p.designStyle,
        thumb: p.thumbnail,
        vizdomId: p.vizdomId,
      })),
    [projects]
  );

  // The catalog item for this page's :id. Nothing else: names never pick a project.
  const headerItem = useMemo(
    () => catalogItems.find((x) => String(x.id) === String(projectId)) || null,
    [catalogItems, projectId]
  );

  const projectTitle = headerItem?.name || "";

  // Not published, archived, or a bad id — distinct from "still loading the
  // catalog", which keeps showing the loading state below.
  const projectNotFound = !loadingHeader && !headerItem;
  // Staff see every project on the site, so for them a missing one really is a
  // draft, archived or a bad link. A client may simply not have been given it;
  // their message must not reveal that the project exists.
  const isStaff = String(user?.email || "").toLowerCase().endsWith("@flipspaces.com");

  // Finishes are kept per build. The picker (shown only when the project has
  // more than one build) starts on the current build; a pick left over from
  // another project falls through to this project's current build.
  const [buildId, setBuildId] = useState(null);
  const builds = headerItem?.builds || [];
  const selectedBuild =
    builds.find((b) => b.id === buildId) ||
    builds.find((b) => b.id === headerItem?.currentBuildId) ||
    builds[0] ||
    null;

  useEffect(() => {
    setSelectedServer(currentRegion === "us" ? "us" : "india");
  }, [currentRegion]);

  const hasAnyScreenshots = useMemo(
    () => (screenshotsGroups || []).some((g) => (g?.items || []).length > 0),
    [screenshotsGroups]
  );

  const fetchScreenshots = useCallback(
    async ({ background = false } = {}) => {
      if (!projectId) return;

      if (!background) setInitialLoadingShots(!hasAnyScreenshots);
      if (background) setIsRefreshingShots(true);

      try {
        // The table is the whole list, newest first: replace, never merge.
        setScreenshotsGroups(await listScreenshots(projectId));
      } catch (err) {
        console.error("listScreenshots error", err);
        if (!background && !hasAnyScreenshots) setScreenshotsGroups([]);
      } finally {
        setInitialLoadingShots(false);
        setIsRefreshingShots(false);
      }
    },
    [projectId, hasAnyScreenshots]
  );

  useEffect(() => {
    fetchScreenshots({ background: false });
  }, [fetchScreenshots]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab !== "screenshots" || document.hidden) return;
      fetchScreenshots({ background: true });
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchScreenshots, activeTab]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("click", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const copyProjectLink = () => {
    navigator.clipboard?.writeText(window.location.href);
  };

  const openImage = (url) => {
    if (!url) return;
    if (!isWebUrl(url)) {
      console.warn("[Screenshots] refusing to open non-web URL:", url);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenVizdom = () => {
    const id = String(headerItem?.vizdomId || "").trim();
    if (!id) return;
    const url = `https://vizdom.flipspaces.app/user/project/${encodeURIComponent(id)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openVizwalk = () => {
    if (!headerItem?.currentBuildId) return;

    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    const params = new URLSearchParams({
      build: headerItem.currentBuildId,
      session: `${headerItem.buildKey}-${stamp}`,
    });

    window.open(
      `/${currentRegion}/experience?${params.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleRefresh = async () => {
    if (activeTab === "finishes") {
      finishesPanelRef.current?.refresh();
      return;
    }
    await fetchScreenshots({ background: true });
  };

  const isRefreshing =
    activeTab === "finishes" ? isRefreshingFinishes : isRefreshingShots;

  const category = headerItem?.designStyle || headerItem?.ConstructionType || "—";
  const server = headerItem?.server || "india";
  const serverLabel = server === "us" ? "US Server" : "India Server";
  const areaDisplay = headerItem?.areaSqft ? formatSqft(headerItem.areaSqft) : "";

  const heroImg = headerItem?.thumb || "";

  // Keyed by build so switching builds starts the panel clean instead of
  // briefly showing the previous build's finishes under the new label.
  const finishesPanel = (
    <FinishesPanel
      key={selectedBuild?.id || "none"}
      ref={finishesPanelRef}
      buildKey={selectedBuild?.key || ""}
      onRefreshingChange={setIsRefreshingFinishes}
    />
  );

  return (
    <div className="sg-page">
      <Navbar
        user={user}
        signOut={signOut}
        selectedServer={selectedServer}
        setSelectedServer={setSelectedServer}
      />

      <div className="sg-container">
        <button
          className="sg-back"
          type="button"
          onClick={() => {
            const backUrl = sessionStorage.getItem("SG_BACK_URL");

            if (backUrl) {
              window.location.assign(backUrl);
              return;
            }

            if (window.history.state && window.history.state.idx > 0) {
              navigate(-1);
              return;
            }

            navigate(`/${currentRegion}/showcase`);
          }}
        >
          <img className="sg-backIcon" src={backIcon} alt="" />
          <span>Back to Projects</span>
        </button>

        {projectNotFound ? (
          <div className="sg-topGrid">
            <div className="sg-left">
              <h1 className="sg-title">
                {isStaff ? "This project isn’t on the site" : "This project isn’t available"}
              </h1>
              <p
                className="sg-subText"
                style={{ display: "flex", marginTop: 12 }}
              >
                {isStaff
                  ? "It may be a draft, archived, or the link may be wrong."
                  : "It may not be shared with your account, or the link may be wrong."}
              </p>
            </div>
          </div>
        ) : (
          <>
          <div className="sg-topGrid">
            <div className="sg-left">
              {loadingHeader ? (
                <div className="sg-loading">Loading…</div>
              ) : (
                <>
                  <h1 className="sg-title">{projectTitle || "Project"}</h1>

                  <div className="sg-chips sg-chips-figma">
                    <div className="sg-subText">
                      <span>{category}</span>
                      <span className="sg-subSep">|</span>
                      <span>{areaDisplay || "—"}</span>
                    </div>

                    <div className="sg-serverPill">
                      <img
                        className="sg-flagImg"
                        src={server === "us" ? usIcon : indiaIcon}
                        alt={server === "us" ? "US" : "IN"}
                      />
                      {serverLabel}
                    </div>
                  </div>

                  <div className="sg-actions">
                    <button
                      type="button"
                      className={`sg-action ${SOLID_LAUNCH_BUTTON ? "sg-action--solid" : ""}`}
                      disabled={!headerItem?.currentBuildId}
                      onClick={openVizwalk}
                    >
                      {SOLID_LAUNCH_BUTTON ? (
                        <span className="sg-actionPlay" aria-hidden="true">▶</span>
                      ) : (
                        <span className="sg-actionIconWrap">
                          <img className="sg-actionIcon" src={vizwalkIcon} alt="" />
                        </span>
                      )}
                      <span className="sg-actionTitle">Open Vizwalk</span>
                    </button>

                    <div className="sg-more">
                      {/* Reuses the old Vizdom button's look, sized to sit beside the action card. */}
                      <button
                        className="sg-vizdomBtn"
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        aria-label="More actions"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen((v) => !v);
                        }}
                      >
                        ⋯
                      </button>

                      {menuOpen && (
                        <div className="sg-menu" role="menu">
                          {String(headerItem?.vizdomId || "").trim() ? (
                            <button className="sg-menuItem" type="button" role="menuitem" onClick={handleOpenVizdom}>
                              Open in Vizdom
                              <img className="sg-vizdomIcon" src={openIcon} alt="" />
                            </button>
                          ) : null}
                          <button className="sg-menuItem" type="button" role="menuitem" onClick={copyProjectLink}>
                            Copy project link
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="sg-hero">
              <ImageWithFallback
                src={heroImg}
                alt={projectTitle}
                className="sg-heroImg"
              />
            </div>
          </div>

          <div className="sg-panel">
            <div className="sg-panelTop">
              <div className="sg-tabs" role="tablist">
                {PROJECT_TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === t.key}
                    className={`sg-tab ${activeTab === t.key ? "isActive" : ""}`}
                    onClick={() => setActiveTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {activeTab !== "media" && (
                <div className="sg-refreshWrap">
                  <button
                    className={`sg-refresh ${isRefreshing ? "isRefreshing" : ""}`}
                    type="button"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                  >
                    <img
                      src={refreshIcon}
                      alt=""
                      className={`sg-refreshIcon ${isRefreshing ? "spin" : ""}`}
                      aria-hidden="true"
                    />
                    <span>{isRefreshing ? "Refreshing" : "Refresh"}</span>
                  </button>
                </div>
              )}
            </div>

            <div className="sg-panelBody">
              {activeTab === "media" ? (
                headerItem?.media?.length ? (
                  <MediaBrowser
                    key={headerItem.id}
                    project={{ name: projectTitle, media: headerItem.media }}
                  />
                ) : (
                  <div className="sg-empty">No media for this project yet</div>
                )
              ) : activeTab === "finishes" ? (
                builds.length > 1 ? (
                  <div className="sg-finishes">
                    <Select
                      className="sg-buildPick"
                      label="Finishes for build"
                      value={selectedBuild?.id || ""}
                      onChange={setBuildId}
                      options={builds.map((b) => ({
                        value: b.id,
                        label: buildOptionLabel(b, headerItem.currentBuildId),
                      }))}
                    />
                    {finishesPanel}
                  </div>
                ) : (
                  finishesPanel
                )
              ) : !hasAnyScreenshots && initialLoadingShots ? (
                <div className="sg-empty">Loading screenshots…</div>
              ) : !hasAnyScreenshots ? (
                <div className="sg-empty">No screenshots available yet</div>
              ) : (
                <div className="sg-groups">
                  {screenshotsGroups.map((group, idx) => {
                    const items = group.items || [];
                    if (!items.length) return null;

                    return (
                      <div className="sg-group" key={group.group || idx}>
                        <div className="sg-groupHeader">
                          <div className="sg-groupLeft">
                            <img className="sg-calIcon" src={calendarIcon} alt="" aria-hidden="true" />
                            <span className="sg-date">
                              {prettyDate(group.ts || group.group)}
                            </span>
                            <span className="sg-count">
                              {items.length} {items.length === 1 ? "image" : "images"}
                            </span>
                          </div>

                          <button
                            type="button"
                            className="sg-downloadAll"
                            onClick={() => items.forEach((img) => downloadFile(img.url))}
                          >
                            <img className="sg-downloadAllIcon" src={downloadAllIcon} alt="" />
                            Download All
                          </button>
                        </div>

                        <div className="sg-grid">
                          {items.map((img, i) => (
                            <div
                              key={(img.id || img.url || "") + i}
                              className="sg-shot"
                              onClick={() => openImage(img.url)}
                              role="button"
                              tabIndex={0}
                            >
                              <ImageWithFallback
                                src={img.url}
                                alt="Screenshot"
                                className="sg-shotImg"
                              />

                              <div className="sg-shotActions">
                                <button
                                  type="button"
                                  className="sg-shotBtn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openImage(img.url);
                                  }}
                                  title="Maximize"
                                >
                                  <img src={maximizeIcon} alt="" />
                                </button>

                                <button
                                  type="button"
                                  className="sg-shotBtn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadFile(img.url);
                                  }}
                                  title="Download"
                                >
                                  <img src={downloadIcon} alt="" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          </>
        )}

        <div style={{ height: 40 }} />
      </div>

      <Footer />
    </div>
  );
}